import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type ReactElement,
} from 'react';
import { api } from './api-client';

type User = { userId: string; email: string; name: string };

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        const refreshed = await api.tryRefresh();
        if (refreshed) {
          const profile = await api.fetch<User>('/profile');
          setUser(profile);
        }
      } catch {
        // Not authenticated — leave user as null
      } finally {
        setIsLoading(false);
      }
    };
    void init();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const data = await api.fetch<{ userId: string; accessToken: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    api.setToken(data.accessToken);
    const profile = await api.fetch<User>('/profile');
    setUser(profile);
  }, []);

  const register = useCallback(
    async (email: string, password: string, name: string): Promise<void> => {
      await api.fetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      });
      await login(email, password);
    },
    [login],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.fetch('/auth/logout', { method: 'POST' });
    } finally {
      api.setToken(null);
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
